
import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format, parseISO, differenceInDays, addDays, isBefore, isAfter, startOfWeek, endOfWeek, eachDayOfInterval, isValid } from "date-fns";
import { ChevronLeft, ChevronRight, Plus, ZoomIn, ZoomOut, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useOrganization } from "@/contexts/OrganizationContext";
import { TaskForm } from "./TaskForm";
import { PhaseForm } from "./PhaseForm";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

interface ProjectGanttChartProps {
  projectId: string;
  phases: any[];
  tasks: any[];
  onTasksChange: (tasks: any[]) => void;
  onPhasesChange: (phases: any[]) => void;
  userRole?: string | null;
}

export const ProjectGanttChart = ({
  projectId,
  phases,
  tasks,
  onTasksChange,
  onPhasesChange,
  userRole
}: ProjectGanttChartProps) => {
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();
  const [dates, setDates] = useState<Date[]>([]);
  const [visibleStartDate, setVisibleStartDate] = useState<Date | null>(null);
  const [visibleEndDate, setVisibleEndDate] = useState<Date | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1); // 1: week, 2: 2 weeks, 3: month
  const [isPhaseFormOpen, setIsPhaseFormOpen] = useState(false);
  const [isTaskFormOpen, setIsTaskFormOpen] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState<any>(null);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [collapsedPhases, setCollapsedPhases] = useState<Record<string, boolean>>({});
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [phaseToDelete, setPhaseToDelete] = useState<any>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const canEdit = userRole === 'salesman';

  console.log("Gantt Chart - Received phases:", phases);

  const parseDateString = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    
    try {
      const parsedDate = new Date(dateString);
      return isValid(parsedDate) ? parsedDate : null;
    } catch (error) {
      console.error("Error parsing date:", error);
      return null;
    }
  };

  const sortedPhases = [...phases].map(phase => ({
    ...phase,
    parsedStartDate: parseDateString(phase.start_date),
    parsedEndDate: parseDateString(phase.end_date)
  })).sort((a, b) => {
    if (!a.parsedStartDate) return 1;
    if (!b.parsedStartDate) return -1;
    return a.parsedStartDate.getTime() - b.parsedStartDate.getTime();
  });

  console.log("Sorted and parsed phases:", sortedPhases);

  const getTasksByPhase = (phaseId: string) => {
    return tasks.filter(task => task.phase_id === phaseId).sort((a, b) => {
      if (!a.start_date) return 1;
      if (!b.start_date) return -1;
      return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
    });
  };

  useEffect(() => {
    if (phases.length > 0) {
      console.log("Phases data:", phases);
      phases.forEach(phase => {
        console.log(`Phase ${phase.name}:`, {
          start_date: phase.start_date,
          end_date: phase.end_date,
          valid_start: phase.start_date ? isValid(new Date(phase.start_date)) : false,
          valid_end: phase.end_date ? isValid(new Date(phase.end_date)) : false
        });
      });
    } else {
      console.log("No phases available");
    }
  }, [phases]);

  useEffect(() => {
    if (phases.length === 0 && tasks.length === 0) {
      const today = new Date();
      const startOfToday = startOfWeek(today);
      setVisibleStartDate(startOfToday);
      setVisibleEndDate(addDays(startOfToday, 14));
      return;
    }

    let earliestDate: Date | null = null;
    let latestDate: Date | null = null;

    for (const phase of phases) {
      if (phase.start_date) {
        const startDate = parseDateString(phase.start_date);
        if (startDate && (!earliestDate || isBefore(startDate, earliestDate))) {
          earliestDate = startDate;
        }
      }
      if (phase.end_date) {
        const endDate = parseDateString(phase.end_date);
        if (endDate && (!latestDate || isAfter(endDate, latestDate))) {
          latestDate = endDate;
        }
      }
    }

    for (const task of tasks) {
      if (task.start_date) {
        const startDate = parseDateString(task.start_date);
        if (startDate && (!earliestDate || isBefore(startDate, earliestDate))) {
          earliestDate = startDate;
        }
      }
      if (task.due_date) {
        const endDate = parseDateString(task.due_date);
        if (endDate && (!latestDate || isAfter(endDate, latestDate))) {
          latestDate = endDate;
        }
      }
    }

    if (!earliestDate || !latestDate) {
      const today = new Date();
      earliestDate = startOfWeek(today);
      latestDate = addDays(earliestDate, 14);
    }

    if (differenceInDays(latestDate, earliestDate) < 14) {
      latestDate = addDays(earliestDate, 14);
    }

    console.log("Setting date range:", earliestDate, "to", latestDate);
    setVisibleStartDate(earliestDate);
    setVisibleEndDate(latestDate);
  }, [phases, tasks]);

  useEffect(() => {
    if (visibleStartDate && visibleEndDate) {
      const range = eachDayOfInterval({
        start: visibleStartDate,
        end: visibleEndDate
      });
      setDates(range);
    }
  }, [visibleStartDate, visibleEndDate, zoomLevel]);

  const handlePrevPeriod = () => {
    if (!visibleStartDate || !visibleEndDate) return;
    
    const days = differenceInDays(visibleEndDate, visibleStartDate);
    setVisibleStartDate(addDays(visibleStartDate, -days));
    setVisibleEndDate(addDays(visibleEndDate, -days));
  };

  const handleNextPeriod = () => {
    if (!visibleStartDate || !visibleEndDate) return;
    
    const days = differenceInDays(visibleEndDate, visibleStartDate);
    setVisibleStartDate(addDays(visibleStartDate, days));
    setVisibleEndDate(addDays(visibleEndDate, days));
  };

  const handleZoomIn = () => {
    if (zoomLevel < 3) {
      setZoomLevel(zoomLevel + 1);
      if (visibleStartDate && visibleEndDate) {
        const currentDays = differenceInDays(visibleEndDate, visibleStartDate);
        const newEndDate = addDays(visibleStartDate, currentDays * 1.5);
        setVisibleEndDate(newEndDate);
      }
    }
  };

  const handleZoomOut = () => {
    if (zoomLevel > 1) {
      setZoomLevel(zoomLevel - 1);
      if (visibleStartDate && visibleEndDate) {
        const currentDays = differenceInDays(visibleEndDate, visibleStartDate);
        const newEndDate = addDays(visibleStartDate, currentDays / 1.5);
        setVisibleEndDate(newEndDate);
      }
    }
  };

  const calculateItemPosition = (
    startDate: string | null | undefined, 
    endDate: string | null | undefined
  ) => {
    if (!startDate || !visibleStartDate || !visibleEndDate || !dates.length) {
      return { left: 0, width: 0 };
    }

    try {
      const start = parseDateString(startDate);
      const end = endDate ? parseDateString(endDate) : (start ? addDays(start, 1) : null);
      
      if (!start || !end) {
        console.warn("Invalid date detected:", { startDate, endDate });
        return { left: 0, width: 0 };
      }
      
      const totalDays = differenceInDays(visibleEndDate, visibleStartDate) || 1;
      const startPosition = differenceInDays(start, visibleStartDate);
      const duration = differenceInDays(end, start) || 1;
      
      const left = (startPosition / totalDays) * 100;
      const width = (duration / totalDays) * 100;
      
      return { 
        left: Math.max(0, Math.min(100, left)), 
        width: Math.min(width, 100 - left) 
      };
    } catch (error) {
      console.error("Error calculating position:", { startDate, endDate, error });
      return { left: 0, width: 0 };
    }
  };

  const getTaskColor = (task: any) => {
    switch (task.priority) {
      case 'high':
        return 'bg-red-400';
      case 'medium':
        return 'bg-yellow-400';
      case 'low':
        return 'bg-green-400';
      default:
        return 'bg-blue-400';
    }
  };

  const getPhaseColor = () => {
    return 'bg-primary/20';
  };

  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const handleEditPhase = (phase: any) => {
    if (!canEdit) return;
    setSelectedPhase(phase);
    setIsPhaseFormOpen(true);
  };

  const handleEditTask = (task: any) => {
    if (!canEdit) return;
    setSelectedTask(task);
    setIsTaskFormOpen(true);
  };

  const addNewPhase = () => {
    if (!canEdit) return;
    setSelectedPhase(null);
    setIsPhaseFormOpen(true);
  };

  const toggleCollapsePhase = (phaseId: string) => {
    setCollapsedPhases(prev => ({
      ...prev,
      [phaseId]: !prev[phaseId]
    }));
  };

  const handleDeletePhase = async () => {
    if (!phaseToDelete || !canEdit) return;
    
    try {
      const { error } = await supabase.functions.invoke('project-tracking-operations', {
        body: {
          action: "deletePhase",
          phaseId: phaseToDelete.id,
          organizationId: currentOrganization?.id,
          userId: supabase.auth.getUser().then(res => res.data.user?.id)
        },
      });
      
      if (error) throw error;
      
      toast({
        title: "Phase deleted",
        description: "The phase has been deleted successfully"
      });
      
      // Remove the phase from the local state
      onPhasesChange(phases.filter(p => p.id !== phaseToDelete.id));
      
      // Update tasks that were in this phase to have no phase
      const updatedTasks = tasks.map(task => 
        task.phase_id === phaseToDelete.id ? { ...task, phase_id: null } : task
      );
      onTasksChange(updatedTasks);
      
    } catch (error: any) {
      console.error("Error deleting phase:", error);
      toast({
        title: "Error",
        description: "Failed to delete phase",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setPhaseToDelete(null);
    }
  };

  const confirmDeletePhase = (phase: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canEdit) return;
    
    setPhaseToDelete(phase);
    setIsDeleteDialogOpen(true);
  };

  // Calculate the earliest start and latest end dates for a phase based on its tasks
  const calculatePhaseTimespan = (phaseId: string) => {
    const phaseTasks = getTasksByPhase(phaseId);
    if (phaseTasks.length === 0) return null;
    
    let earliestStart = null;
    let latestEnd = null;
    
    for (const task of phaseTasks) {
      const startDate = task.start_date ? parseDateString(task.start_date) : null;
      const endDate = task.due_date ? parseDateString(task.due_date) : null;
      
      if (startDate && (!earliestStart || isBefore(startDate, earliestStart))) {
        earliestStart = startDate;
      }
      
      if (endDate && (!latestEnd || isAfter(endDate, latestEnd))) {
        latestEnd = endDate;
      }
    }
    
    return { start: earliestStart, end: latestEnd };
  };

  return (
    <div ref={chartContainerRef} className="border rounded-md">
      <div className="flex justify-between items-center p-4 border-b">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevPeriod}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPeriod}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium px-2 py-1">
            {visibleStartDate && visibleEndDate ? 
              `${format(visibleStartDate, 'MMM d, yyyy')} - ${format(visibleEndDate, 'MMM d, yyyy')}` : 
              'Loading...'}
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleZoomOut}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleZoomIn}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          {canEdit && (
            <Button size="sm" onClick={addNewPhase}>
              <Plus className="h-4 w-4 mr-1" />
              Add Phase
            </Button>
          )}
        </div>
      </div>

      <div className="gantt-container overflow-x-auto">
        <div className="flex">
          <div className="w-1/4 min-w-[200px] max-w-[300px] border-r">
            <div className="h-10 border-b sticky top-0 bg-background flex items-center px-4 font-medium">
              Task / Phase
            </div>
            {sortedPhases.length === 0 ? (
              <div className="px-4 py-2 text-muted-foreground italic">
                No phases yet. Click "Add Phase" to create one.
              </div>
            ) : (
              sortedPhases.map((phase) => (
                <div key={phase.id}>
                  <div 
                    className="px-4 py-2 border-b font-medium cursor-pointer hover:bg-muted/50 flex justify-between items-center h-10"
                    onClick={() => handleEditPhase(phase)}
                  >
                    <div className="flex items-center flex-1 min-w-0">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="p-0 h-6 w-6 mr-2 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCollapsePhase(phase.id);
                        }}
                      >
                        {collapsedPhases[phase.id] ? (
                          <ChevronRight className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="truncate">{phase.name}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{phase.name}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    {canEdit && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="p-0 h-6 w-6 opacity-50 hover:opacity-100 hover:bg-destructive/10 flex-shrink-0"
                        onClick={(e) => confirmDeletePhase(phase, e)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                  {!collapsedPhases[phase.id] && getTasksByPhase(phase.id).map((task) => (
                    <div 
                      key={task.id} 
                      className="px-4 py-2 border-b pl-8 text-sm cursor-pointer hover:bg-muted/50 h-10 flex items-center"
                      onClick={() => handleEditTask(task)}
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="truncate block">{task.title}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{task.title}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>

          <div className="flex-1 relative">
            <div className="flex border-b h-10 sticky top-0 bg-background">
              {dates.map((date, index) => (
                <div 
                  key={index} 
                  className={`text-center text-xs flex-1 py-2 border-r ${isWeekend(date) ? 'bg-muted/30' : ''}`}
                  style={{ minWidth: '40px' }}
                >
                  <div>{format(date, 'EEE')}</div>
                  <div>{format(date, 'd')}</div>
                </div>
              ))}
            </div>

            <div className="relative">
              {sortedPhases.map((phase) => (
                <div key={phase.id}>
                  <div className="relative h-10 border-b">
                    {phase.start_date && phase.end_date && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div 
                            className={`absolute h-6 top-2 rounded ${getPhaseColor()} cursor-pointer`}
                            style={{
                              left: `${calculateItemPosition(phase.start_date, phase.end_date).left}%`,
                              width: `${calculateItemPosition(phase.start_date, phase.end_date).width}%`,
                            }}
                            onClick={() => handleEditPhase(phase)}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="text-sm font-medium">{phase.name}</div>
                          <div className="text-xs">
                            {phase.start_date && format(new Date(phase.start_date), 'MMM d, yyyy')} - 
                            {phase.end_date && format(new Date(phase.end_date), 'MMM d, yyyy')}
                          </div>
                          {phase.description && (
                            <div className="text-xs max-w-xs">{phase.description}</div>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>

                  {!collapsedPhases[phase.id] ? (
                    // Show individual tasks when not collapsed
                    getTasksByPhase(phase.id).map((task) => (
                      <div key={task.id} className="relative h-10 border-b">
                        {task.start_date && task.due_date && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div 
                                className={`absolute h-4 top-3 rounded ${getTaskColor(task)} cursor-pointer`}
                                style={{
                                  left: `${calculateItemPosition(task.start_date, task.due_date).left}%`,
                                  width: `${calculateItemPosition(task.start_date, task.due_date).width}%`,
                                }}
                                onClick={() => handleEditTask(task)}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-sm font-medium">{task.title}</div>
                              <div className="text-xs">
                                {task.start_date && format(new Date(task.start_date), 'MMM d, yyyy')} - 
                                {task.due_date && format(new Date(task.due_date), 'MMM d, yyyy')}
                              </div>
                              <div className="text-xs flex gap-2 mt-1">
                                <Badge variant="outline">{task.status}</Badge>
                                {task.priority && (
                                  <Badge>{task.priority}</Badge>
                                )}
                              </div>
                              {task.description && (
                                <div className="text-xs max-w-xs mt-1">{task.description}</div>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    ))
                  ) : (
                    // Draw composite timeline for all tasks in the phase when collapsed
                    (() => {
                      const timespan = calculatePhaseTimespan(phase.id);
                      const phaseTasks = getTasksByPhase(phase.id);
                      
                      if (!timespan || !timespan.start || !timespan.end || phaseTasks.length === 0) {
                        return null;
                      }
                      
                      return (
                        <div className="relative h-10 border-b">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div 
                                className="absolute h-4 top-3 rounded bg-blue-400 cursor-pointer"
                                style={{
                                  left: `${calculateItemPosition(timespan.start.toISOString(), timespan.end.toISOString()).left}%`,
                                  width: `${calculateItemPosition(timespan.start.toISOString(), timespan.end.toISOString()).width}%`,
                                }}
                                onClick={() => toggleCollapsePhase(phase.id)}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-sm font-medium">{phase.name} Tasks</div>
                              <div className="text-xs">
                                {format(timespan.start, 'MMM d, yyyy')} - {format(timespan.end, 'MMM d, yyyy')}
                              </div>
                              <div className="text-xs mt-1">
                                Contains {phaseTasks.length} task{phaseTasks.length !== 1 ? 's' : ''}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      );
                    })()
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isPhaseFormOpen} onOpenChange={setIsPhaseFormOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedPhase ? 'Edit Phase' : 'Add Phase'}</DialogTitle>
          </DialogHeader>
          <PhaseForm
            projectId={projectId}
            initialData={selectedPhase}
            onSuccess={() => {
              toast({
                title: "Success",
                description: `Phase ${selectedPhase ? 'updated' : 'created'} successfully`
              });
              setIsPhaseFormOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={isTaskFormOpen} onOpenChange={setIsTaskFormOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTask ? 'Edit Task' : 'Add Task'}</DialogTitle>
          </DialogHeader>
          <TaskForm
            projectId={projectId}
            phases={phases}
            initialData={selectedTask}
            onSuccess={() => {
              toast({
                title: "Success",
                description: `Task ${selectedTask ? 'updated' : 'created'} successfully`
              });
              setIsTaskFormOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeletePhase}
        title="Delete Phase"
        description={`Are you sure you want to delete the phase "${phaseToDelete?.name}"? This will remove the phase but not its tasks.`}
      />

      <style>
        {`
          .gantt-container {
            max-height: calc(100vh - 250px);
            overflow-y: auto;
          }
        `}
      </style>
    </div>
  );
};
